import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import PluginCardC from '../components/PluginCardC';
import NewsletterSimpleC from '../components/NewsletterSimpleC';
import { useTranslation } from '../hooks/useTranslation';
import { useOwnedProductIds } from '../hooks/useOwnedProductIds';
import config from '../config';

interface Plugin {
  id: string;
  name: string;
  slug: string;
  description: string;
  detailedDescription?: string;
  price: number;
  version: string;
  imageUrl?: string;
  features?: string[];
  compatibility: string[];
  tags: string[];
  isActive: boolean;
}

interface PluginsResponse {
  success: boolean;
  data: Plugin[];
  count: number;
  message?: string;
}

function Plugins() {
  const { t, currentLanguage } = useTranslation();
  const ownedIds = useOwnedProductIds();
  const navigate = useNavigate();
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isNewsletterSubscribed, setIsNewsletterSubscribed] = useState(false);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    
    // Check newsletter subscription status
    const newsletterSubscribed = localStorage.getItem('vst_shop_newsletter_subscribed');
    setIsNewsletterSubscribed(newsletterSubscribed === 'true');
    
    // Load plugins
    loadPlugins();
  }, [currentLanguage]);

  const loadPlugins = async () => {
    try {
      setLoading(true);
      const response = await axios.get<PluginsResponse>(`${config.api.baseUrl}/api/plugins?lang=${currentLanguage}`);
      
      if (response.data.success) {
        setPlugins(response.data.data);
      } else {
        setError(response.data.message || t('plugins.failed_to_load'));
      }
    } catch (err: any) {
      console.error('Failed to load plugins:', err);
      setError(t('plugins.failed_to_load'));
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = (_pluginId: string, slug: string) => {
    navigate(`/plugins/${slug}`);
  };

  const handleViewDetails = (slug: string) => {
    // Navigate to plugin detail page
    navigate(`/plugins/${slug}`);
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#a855f7' }} />
          <p className="text-white mt-4">{t('plugins.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={loadPlugins}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-md transition-colors duration-200"
          >
            {t('plugins.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section - Full Width */}
      <section 
        className="relative pt-16 bg-cover -mx-4"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/backgrounds/plugins.gif)',
          backgroundPosition: 'center 20%',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      >
        
        <div className="container mx-auto px-4 text-center vertical-bottom relative z-10 flex flex-col justify-center h-full" style={{ paddingTop: '80px' }}>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in mt-24">
            {t('plugins.hero_title')}
            <span className="block bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent leading-tight py-2">
              {t('plugins.hero_subtitle')}
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-2 max-w-3xl mx-auto animate-fade-in">
            {t('plugins.hero_description')}
          </p>
        </div>
      </section>

      {/* Content with proper background and container padding */}
      <div className="bg-gray-900 -mx-4">
      {/* Plugins Grid */}
      <section className="py-16">
          <div className="container mx-auto px-6">
          {plugins.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-400 text-lg mb-4">
                {t('plugins.no_plugins_available')}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {plugins.map((plugin) => (
                <PluginCardC
                  key={plugin.id}
                  id={plugin.id}
                  name={plugin.name}
                  price={plugin.price}
                  imageUrl={plugin.imageUrl}
                  description={plugin.description}
                  version={plugin.version}
                  compatibility={plugin.compatibility}
                  features={plugin.features || []}
                  slug={plugin.slug}
                  tags={plugin.tags}
                  owned={ownedIds.has(String(plugin.id))}
                  onBuyNow={handleBuyNow}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section - show only for unauthenticated unsubscribed users */}
      {!isAuthenticated && !isNewsletterSubscribed && <NewsletterSimpleC />}
      </div>
    </div>
  );
}

export default Plugins; 